import { FormEvent, useEffect, useState } from "react";
import { Download, Eye, FileSpreadsheet, RefreshCw, Search, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { AdminOrderListParams, AdminUser, PaginationMeta, bulkUpdateAdminOrders, exportAdminOrders, getCurrentAdmin, listAdminOrders, logout } from "./api";
import { Order, OrderStatus, PaymentStatus, orderStatusLabels, paymentMethodLabels, paymentStatusLabels } from "@/services/orders";
import Pagination from "./components/Pagination";
import { useConfirmDialog } from "./components/ConfirmDialog";

const defaultPagination: PaginationMeta = {
  Page: 1,
  PerPage: 20,
  Total: 0,
  TotalPages: 1,
};

function isOverdueUnpaid(order: Order): boolean {
  if (order.PaymentStatus !== "Unpaid" || !order.PaymentDueAt) return false;
  if (order.OrderStatus === "Cancelled" || order.OrderStatus === "Completed") return false;
  return new Date(order.PaymentDueAt).getTime() < Date.now();
}

const defaultCounts: Record<OrderStatus, number> = {
  Pending: 0,
  Processing: 0,
  Shipped: 0,
  Completed: 0,
  Cancelled: 0,
};

const defaultPaymentCounts: Record<PaymentStatus, number> = {
  Unpaid: 0,
  Paid: 0,
  Refunded: 0,
  PartiallyRefunded: 0,
};

const statusBadgeClass: Record<string, string> = {
  Pending: "bg-[#f3ecdf] text-[#8a6a2f]",
  Processing: "bg-[#e6eefb] text-[#2a5ca8]",
  Shipped: "bg-[#e9f4ec] text-[#245438]",
  Completed: "bg-[#e9f4ec] text-[#245438]",
  Cancelled: "bg-[#fdebea] text-[#9a2d2d]",
};

const bulkOrderStatusOptions: { value: OrderStatus; label: string }[] = [
  { value: "Pending", label: "待處理" },
  { value: "Processing", label: "處理中" },
  { value: "Shipped", label: "已出貨" },
  { value: "Completed", label: "已完成" },
  { value: "Cancelled", label: "已取消" },
];

function escapeCsvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** 匯出目前篩選條件下的訂單清單（不限於當頁），供對帳、出貨作業或跟其他系統核對使用。 */
function downloadOrdersCsv(fileName: string, orders: Order[]) {
  const header = ["訂單編號", "收件人", "電話", "商品件數", "商品小計", "折扣", "運費", "訂單總額", "付款方式", "付款狀態", "訂單狀態", "建立時間"];
  const rows = orders.map((order) =>
    [
      order.OrderNumber,
      order.RecipientName,
      order.RecipientPhone,
      String(order.Items.reduce((sum, item) => sum + item.Quantity, 0)),
      String(order.SubtotalAmount),
      String(order.DiscountAmount),
      String(order.ShippingFee),
      String(order.TotalAmount),
      order.PaymentMethodName || paymentMethodLabels[order.PaymentMethod] || order.PaymentMethod,
      paymentStatusLabels[order.PaymentStatus],
      orderStatusLabels[order.OrderStatus],
      order.CreatedAt || "",
    ]
      .map(escapeCsvField)
      .join(",")
  );
  const csvContent = "﻿" + [header.join(","), ...rows].join("\r\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

type Filters = Required<Pick<AdminOrderListParams, "OrderStatus" | "PaymentStatus" | "Keyword" | "StartDate" | "EndDate" | "Page" | "PerPage">>;

export default function AdminOrders() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialKeyword = searchParams.get("Keyword") || "";
  const initialStatus = searchParams.get("status") || "";
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [counts, setCounts] = useState<Record<OrderStatus, number>>(defaultCounts);
  const [paymentCounts, setPaymentCounts] = useState<Record<PaymentStatus, number>>(defaultPaymentCounts);
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [orderStatus, setOrderStatus] = useState(initialStatus);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [keyword, setKeyword] = useState(initialKeyword);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkTargetStatus, setBulkTargetStatus] = useState<OrderStatus>("Processing");
  const [bulkApplying, setBulkApplying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  function currentFilters(overrides: Partial<Filters> = {}): Filters {
    return {
      OrderStatus: orderStatus,
      PaymentStatus: paymentStatus,
      Keyword: keyword.trim(),
      StartDate: startDate,
      EndDate: endDate,
      Page: pagination.Page,
      PerPage: pagination.PerPage,
      ...overrides,
    };
  }

  async function load(filters: Filters = currentFilters({ OrderStatus: initialStatus, Keyword: initialKeyword, Page: 1 })) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminOrders(filters);
      setOrders(data.Orders);
      setCounts({ ...defaultCounts, ...(data.Counts || {}) });
      setPaymentCounts({ ...defaultPaymentCounts, ...(data.PaymentCounts || {}) });
      setPagination(data.Pagination || defaultPagination);
      setSelectedIds(new Set());
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "訂單資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  const allOnPageSelected = orders.length > 0 && orders.every((order) => selectedIds.has(order.OrderId));

  function toggleSelectOne(orderId: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  }

  function toggleSelectAllOnPage() {
    setSelectedIds((current) => {
      if (allOnPageSelected) {
        const next = new Set(current);
        orders.forEach((order) => next.delete(order.OrderId));
        return next;
      }
      const next = new Set(current);
      orders.forEach((order) => next.add(order.OrderId));
      return next;
    });
  }

  const selectedCount = selectedIds.size;

  async function handleBulkStatusApply() {
    const targetLabel = orderStatusLabels[bulkTargetStatus];
    const confirmed = await confirm({
      title: `批次變更 ${selectedCount} 筆訂單狀態？`,
      description: (
        <>
          即將把已選取的 <strong className="font-bold text-[#203d32]">{selectedCount}</strong> 筆訂單，訂單狀態一律改為「
          <strong className="font-bold text-[#203d32]">{targetLabel}</strong>」。
          {bulkTargetStatus === "Cancelled" && <><br />改為已取消的訂單將自動歸還商品庫存，此操作請確認無誤。</>}
        </>
      ),
      confirmText: "套用變更",
      cancelText: "取消",
      tone: bulkTargetStatus === "Cancelled" ? "danger" : "info",
    });
    if (!confirmed) return;

    setBulkApplying(true);
    setPageMessage("");
    try {
      const result = await bulkUpdateAdminOrders({ OrderIds: Array.from(selectedIds), OrderStatus: bulkTargetStatus });
      setPageMessage(`已更新 ${result.UpdatedCount} 筆訂單狀態。`);
      await load(currentFilters());
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "批次更新失敗，請稍後再試。");
    } finally {
      setBulkApplying(false);
    }
  }

  async function handleBulkMarkPaid() {
    const confirmed = await confirm({
      title: `批次標記 ${selectedCount} 筆訂單為已付款？`,
      description: "即將把已選取的訂單付款狀態一律改為「已付款」，訂單狀態本身不會被更動。",
      confirmText: "標記為已付款",
      cancelText: "取消",
      tone: "info",
    });
    if (!confirmed) return;

    setBulkApplying(true);
    setPageMessage("");
    try {
      const result = await bulkUpdateAdminOrders({ OrderIds: Array.from(selectedIds), PaymentStatus: "Paid" as PaymentStatus });
      setPageMessage(`已更新 ${result.UpdatedCount} 筆訂單的付款狀態。`);
      await load(currentFilters());
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "批次更新失敗，請稍後再試。");
    } finally {
      setBulkApplying(false);
    }
  }

  async function handleExportCsv() {
    setExporting(true);
    setPageMessage("");
    try {
      // 匯出目前篩選條件下「全部」符合的訂單，不只是畫面上這一頁，所以用大 PerPage 重新查一次。
      const data = await listAdminOrders(currentFilters({ Page: 1, PerPage: 5000 }));
      downloadOrdersCsv(`orders-${new Date().toISOString().slice(0, 10)}.csv`, data.Orders);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "匯出失敗，請稍後再試。");
    } finally {
      setExporting(false);
    }
  }

  async function handleExportExcel() {
    setExportingExcel(true);
    setPageMessage("");
    try {
      await exportAdminOrders(currentFilters());
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "匯出失敗，請稍後再試。");
    } finally {
      setExportingExcel(false);
    }
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load(currentFilters({ Page: 1 }));
  }

  function handleResetDateRange() {
    setStartDate("");
    setEndDate("");
    load(currentFilters({ StartDate: "", EndDate: "", Page: 1 }));
  }

  const totalOrders = Object.values(counts).reduce((sum, value) => sum + value, 0);
  const totalByPayment = Object.values(paymentCounts).reduce((sum, value) => sum + value, 0);

  const statusTabs: { value: string; label: string; count: number }[] = [
    { value: "", label: "全部訂單", count: totalOrders },
    { value: "Pending", label: "待處理", count: counts.Pending },
    { value: "Processing", label: "處理中", count: counts.Processing },
    { value: "Shipped", label: "已出貨", count: counts.Shipped },
    { value: "Completed", label: "已完成", count: counts.Completed },
    { value: "Cancelled", label: "已取消", count: counts.Cancelled },
  ];

  const paymentStatusTabs: { value: string; label: string; count: number }[] = [
    { value: "", label: "不限付款狀態", count: totalByPayment },
    { value: "Unpaid", label: "待付款", count: paymentCounts.Unpaid },
    { value: "Paid", label: "已付款", count: paymentCounts.Paid },
    { value: "PartiallyRefunded", label: "部分退款", count: paymentCounts.PartiallyRefunded },
    { value: "Refunded", label: "已退款", count: paymentCounts.Refunded },
  ];

  return (
    <AdminLayout sectionLabel="訂單管理" title="訂單列表" user={currentUser} onLogout={handleLogout}>
      <p className="mb-1.5 text-xs font-bold text-[#59665f]">訂單狀態</p>
      <div className="mb-3 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {statusTabs.map((tab) => (
          <button
            className={`flex min-w-[104px] shrink-0 items-center justify-between rounded-md border px-3 py-2 text-left transition ${
              orderStatus === tab.value ? "border-[#315746] bg-[#315746] text-white" : "border-[#dfe6e1] bg-white text-[#315746]"
            }`}
            key={tab.value}
            onClick={() => {
              setOrderStatus(tab.value);
              load(currentFilters({ OrderStatus: tab.value, Page: 1 }));
            }}
            type="button"
          >
            <span className="text-xs font-bold">{tab.label}</span>
            <span className="ml-3 text-lg font-bold">{tab.count}</span>
          </button>
        ))}
      </div>

      <p className="mb-1.5 text-xs font-bold text-[#59665f]">付款狀態</p>
      <div className="mb-4 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {paymentStatusTabs.map((tab) => (
          <button
            className={`flex min-w-[104px] shrink-0 items-center justify-between rounded-md border px-3 py-2 text-left transition ${
              paymentStatus === tab.value ? "border-[#315746] bg-[#315746] text-white" : "border-[#dfe6e1] bg-white text-[#315746]"
            }`}
            key={tab.value}
            onClick={() => {
              setPaymentStatus(tab.value);
              load(currentFilters({ PaymentStatus: tab.value, Page: 1 }));
            }}
            type="button"
          >
            <span className="text-xs font-bold">{tab.label}</span>
            <span className="ml-3 text-lg font-bold">{tab.count}</span>
          </button>
        ))}
      </div>

      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:sticky md:top-[102px] md:z-10 md:flex md:flex-wrap md:items-end md:bg-white/95 md:p-4 md:shadow-[0_10px_24px_rgba(32,61,50,.08)] md:backdrop-blur" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 md:min-w-[220px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="order-keyword">關鍵字</label>
          <input id="order-keyword" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="訂單編號、收件人姓名、電話、Email" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <div className="grid gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="order-start">起始日期</label>
          <input id="order-start" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
        </div>
        <div className="grid gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="order-end">結束日期</label>
          <input id="order-end" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
        </div>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          搜尋
        </button>
        {(startDate || endDate) && (
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#59665f] transition hover:bg-[#f0f5f2]" onClick={handleResetDateRange} type="button">
            <X size={16} />
            清除日期
          </button>
        )}
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load(currentFilters())} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-60" disabled={exporting} onClick={handleExportCsv} type="button">
          <Download size={18} />
          {exporting ? "匯出中..." : "匯出 CSV"}
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-60" disabled={exportingExcel} onClick={handleExportExcel} type="button">
          <FileSpreadsheet size={18} />
          {exportingExcel ? "匯出中..." : "匯出 Excel"}
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      {selectedCount > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-[#c7dccf] bg-[#e9f4ec] px-4 py-3">
          <span className="text-sm font-bold text-[#203d32]">已選取 {selectedCount} 筆訂單</span>
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="批次變更為訂單狀態"
              className="h-10 rounded-md border border-[#b6cdbe] bg-white px-3 text-sm font-bold text-[#315746] outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
              value={bulkTargetStatus}
              onChange={(event) => setBulkTargetStatus(event.target.value as OrderStatus)}
            >
              {bulkOrderStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button className="inline-flex h-10 items-center justify-center rounded-md bg-[#315746] px-4 text-sm font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={bulkApplying} onClick={handleBulkStatusApply} type="button">
              套用訂單狀態
            </button>
            <button className="inline-flex h-10 items-center justify-center rounded-md border border-[#b6cdbe] bg-white px-4 text-sm font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-60" disabled={bulkApplying} onClick={handleBulkMarkPaid} type="button">
              標記為已付款
            </button>
            <button className="inline-flex h-10 items-center justify-center rounded-md px-3 text-sm font-bold text-[#59665f] transition hover:text-[#315746]" onClick={() => setSelectedIds(new Set())} type="button">
              取消選取
            </button>
          </div>
        </div>
      )}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[1280px] table-fixed border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[44px] px-4 py-3">
                  <input
                    aria-label="全選本頁"
                    checked={allOnPageSelected}
                    className="h-4 w-4 accent-[#315746]"
                    onChange={toggleSelectAllOnPage}
                    type="checkbox"
                  />
                </th>
                <th className="w-[220px] px-4 py-3">訂單編號</th>
                <th className="w-[200px] px-4 py-3">收件人</th>
                <th className="w-[120px] px-4 py-3">商品件數</th>
                <th className="w-[140px] px-4 py-3">金額</th>
                <th className="w-[160px] px-4 py-3">付款方式 / 狀態</th>
                <th className="w-[110px] px-4 py-3">訂單狀態</th>
                <th className="w-[100px] px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={8}>載入中...</td></tr>
              ) : orders.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={8}>沒有符合條件的訂單。</td></tr>
              ) : orders.map((order) => (
                <tr className={`border-t border-[#edf0ee] ${selectedIds.has(order.OrderId) ? "bg-[#f3f8f5]" : ""}`} key={order.OrderId}>
                  <td className="px-4 py-4">
                    <input
                      aria-label={`選取訂單 ${order.OrderNumber}`}
                      checked={selectedIds.has(order.OrderId)}
                      className="h-4 w-4 accent-[#315746]"
                      onChange={() => toggleSelectOne(order.OrderId)}
                      type="checkbox"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <p className="font-bold text-[#203d32]">{order.OrderNumber}</p>
                    <p className="mt-1 text-xs text-[#7b877f]">{order.CreatedAt}</p>
                    {order.MemberId && <span className="mt-1 inline-flex items-center rounded-full bg-[#e9f4ec] px-2 py-0.5 text-[11px] font-bold text-[#245438]">會員</span>}
                  </td>
                  <td className="px-4 py-4">
                    <p className="font-bold text-[#203d32]">{order.RecipientName}</p>
                    <p className="mt-1 text-xs text-[#7b877f]">{order.RecipientPhone}</p>
                  </td>
                  <td className="px-4 py-4">{order.Items.reduce((sum, item) => sum + item.Quantity, 0)} 件</td>
                  <td className="px-4 py-4 font-bold text-[#203d32]">NT$ {order.TotalAmount.toLocaleString()}</td>
                  <td className="px-4 py-4">
                    <div>{order.PaymentMethodName || paymentMethodLabels[order.PaymentMethod] || order.PaymentMethod}</div>
                    <div className="mt-1 text-xs text-[#7b877f]">{paymentStatusLabels[order.PaymentStatus]}</div>
                    {isOverdueUnpaid(order) && (
                      <span className="mt-1 inline-flex rounded-full bg-[#fdebea] px-2 py-0.5 text-[11px] font-bold text-[#b3261e]">已逾期未付款</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${statusBadgeClass[order.OrderStatus] || ""}`}>
                      {orderStatusLabels[order.OrderStatus]}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => navigate(`/admin/orders/${order.OrderId}`)} type="button">
                      <Eye size={16} />查看
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load(currentFilters({ Page: page }))} />
      </div>
      <ConfirmDialog />
    </AdminLayout>
  );
}
